/**
 * Organization query-invalidation helpers plus the org-switch action.
 * Extracted from useOrganizations for the 150-line rule.
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/application/queries";
import { useSwitchOrganization } from "./useSwitchOrganization";
import { useCurrentOrganizationId } from "./useCurrentOrganizationId";

export const useOrganizationActions = () => {
  const queryClient = useQueryClient();
  const orgId = useCurrentOrganizationId();

  const switchOrganizationMutation = useSwitchOrganization();

  const loadUserOrganizations = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.organization.userOrganizations(),
    });
  }, [queryClient]);

  const loadOrganizationDetails = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.organization.details(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationUsers = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.users(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationInvitations = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.invitations(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationTemplates = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.templates(orgId),
    });
  }, [queryClient, orgId]);

  const dispatchSwitchOrganization = useCallback(
    async (organizationId: string | number) => {
      try {
        return await switchOrganizationMutation.mutateAsync(organizationId);
      } catch {
        return;
      }
    },
    [switchOrganizationMutation],
  );

  return {
    loadUserOrganizations,
    loadOrganizationDetails,
    loadOrganizationUsers,
    loadOrganizationInvitations,
    loadOrganizationTemplates,
    switchOrganization: dispatchSwitchOrganization,
  };
};
